// Copyright (c) Hamilton Manufacturing Corp.

import React, { createContext, useReducer, useContext, useEffect, useState, } from 'react';
import Modal from '../Modal';
import { clearAllPersistedState, UseDeviceId, usePersistedState } from '../api/usePersistedState';
import { HamQuestGiver } from '../api/HamQuest';
import { QuestRoute } from '../api/QuestRoutes';
import { Enum, decodeJWT, isMobile } from '../api/utility';
import initialState, { sanitizeSiteConfig } from '../../../InitialState';
import { getNotifPermissions } from '../../_pwa/pages/Notifications';
import Button from '../form/Button';
import Loading from '../Loading';
import { HamQuest } from '../../../App';

export const GlobalContext = createContext(initialState);

export const QUEST_KEYS = 'HSQuestKeys';

const ActionType = new Enum([
    'REHYDRATE_STATE',
    'SET_ACCESS_TOKEN',
    'SET_APP_KEY',
    'SET_CONFIG',
    'SET_LOCATIONS',
    'SET_GUEST_LOGIN',
    'SET_WASH_CLUB',
    'SET_STATES',
    'SET_VEHICLE',
    'SET_ACCOUNT_INFO',
    'SET_CONFIRMED_EMAIL',
    'SET_PROCESSOR',
    'SET_USER_LOCATION',
    'SET_ACCOUNT_PAYMENT',
    'SET_UNREAD_NOTIFICATIONS',
    'SET_NOTIFICATIONS',
    'CLEAR_NOTIFICATIONS',
    'ADD_GIFT_EMAIL',
    'SET_FORCED_LOCATION',
    'SET_ACTIVE_USER',
    'CLEAR_ACTIVE_USER',
    'SET_USER_WALLET',
    'SET_OFFLINE',
    'SET_THEME',
    'SET_LIGHT_THEME',
    'SET_DARK_THEME',
    'SET_THEMES',
    'SET_HOME_CARDS',
    'SET_BUBBLES',
    'SET_FONT_SIZE',
    'SET_LOADING',
    'SET_CART'
]);

const GlobalStateProvider = ({ children, initialValues = initialState }) => {
    // clearAllPersistedState();

    const [persistedState, setPersistedState] = usePersistedState('global', initialValues);
    const [state, dispatch] = useReducer(Reducer, persistedState);

    useEffect(() => {
        setPersistedState(state);
    }, [setPersistedState, state]);

    useEffect(() => {
        let timeoutId = null;

        const handleStorageChange = event => {
            if (event.key.match('global')) {
                // if (timeoutId) {
                //     clearTimeout(timeoutId);
                // }

                // timeoutId = setTimeout(() => {
                const updatedState = JSON.parse(event.newValue);
                dispatch({ type: ActionType.REHYDRATE_STATE, payload: updatedState });
                // }, 1000);
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    return (
        <GlobalContext.Provider value={{ state, dispatch }}>
            {children}
        </GlobalContext.Provider>
    );
}

export function useGlobalState() {
    const { state, dispatch } = useContext(GlobalContext);

    const [functions] = useState({
        getQuestKeys: () => {
            try {
                const keys = window.localStorage.getItem(QUEST_KEYS);
                const parsedKeys = JSON.parse(keys);
                return parsedKeys;
            } catch {
                console.warn('No Auth keys...')
                return {};
            }
        },

        getCacheQuestKeys: async () => {
            if (!('caches' in window)) {
                console.error('Cache API not supported');
                return functions.getQuestKeys()
            }

            try {
                const cache = await caches.open(QUEST_KEYS);
                const response = await cache.match('/' + QUEST_KEYS);

                if (!response) {
                    console.warn('No refresh token found');
                    return functions.getQuestKeys();
                }
                const tokens = await response.json();
                console.log('Retrieved successfully');
                return tokens;
            } catch (error) {
                console.error('Failed to retrieve the refresh token:', error);
            }
        },

        setCacheQuestKeys: async (refresh, access) => {
            if (!('caches' in window)) {
                throw new Error('Cache API not supported');
            }

            try {
                const cache = await caches.open(QUEST_KEYS);
                const payload = JSON.stringify({ access, refresh });

                const response = new Response(payload, { headers: { 'Content-Type': 'application/json' } });

                await cache.put('/' + QUEST_KEYS, response);
                console.log("Cache successful");
            } catch (error) {
                console.error('Failed to store the refresh token:', error);
            }
        },

        setQuestKeys: (refresh, access) => {
            const state = {
                access,
                refresh
            }
            window.localStorage.setItem(QUEST_KEYS, JSON.stringify(state));
            functions.setCacheQuestKeys(refresh, access);
        },
        updateAccessToken: () => {
            return new Promise((resolve, reject) => {
                try {
                    functions.getCacheQuestKeys()
                        .then(res => {
                            if (!res || !res.access || !res.refresh) {
                                if (state.active_user.isLoggedIn) functions.logOut();
                                return;
                            }

                            HamQuestGiver.put(process.env.REACT_APP_API_URL + QuestRoute.user.refreshToken, {
                                accessToken: res.access,
                                refreshToken: res.refresh,
                            }, {
                                headers: { app_key: state.app_key }
                            })
                                .then(res => {
                                    functions.updateLogIn(res);
                                    resolve(res);
                                })
                                .catch(er => {
                                    functions.logOut();
                                    Modal.Show({
                                        title: 'Logged Out',
                                        body: 'You have been logged out',
                                        type: 'warning'
                                    })
                                    reject(er)
                                })
                        })
                } catch (err) {
                    console.error('Failed to refresh token.')
                    reject(err)
                    throw err;
                }
            })
        },
        updateUnauthorized: () => {
            HamQuest.setUnauthorized((url, config) => {
                return new Promise((resolve, reject) => {
                    functions
                        .updateAccessToken()
                        .then(() => {
                            HamQuest.fetchQuest(url, config)
                                .then(res => resolve(res))
                                .catch(ex => reject(ex))
                        })
                })
            })
        },

        updateLogIn: (res) => {
            const { accessToken, refreshToken } = res;
            if (!accessToken) return;

            const decode = decodeJWT(accessToken);
            functions.setQuestKeys(refreshToken, accessToken);
            dispatch({
                type: ActionType.SET_ACCESS_TOKEN,
                payload: { ...res, isAdmin: decode.IsAdmin === 'true' }
            });
            functions.updateUnauthorized();
            HamQuest.setLifecycle(decode.exp * 1000);
        },

        getSiteKey: subdomain => {
            return new Promise((resolve, reject) => {
                try {
                    functions.setLoading(true);
                    HamQuest.getQuery(QuestRoute.merchant.siteKey, {
                        subdomain: subdomain || 'HamDev'
                    })
                        .then(res => {
                            dispatch({
                                type: ActionType.SET_APP_KEY,
                                payload: res,
                            });
                            HamQuest.addHeaderKeys({ app_key: res });
                            functions.getSiteConfig()
                        })
                        .catch(ex => {
                            reject('We couldn\'t find the site for the specified subdomain. Please check the subdomain and try again.')
                        })
                } catch (err) {
                    console.error('Failed to pull settings.')
                    reject('An unexpected error occurred while loading the site. Please try again later.')
                }
            })
        },

        getSiteConfig: (forced) => {
            return new Promise((resolve, reject) => {
                try {
                    HamQuest.get(QuestRoute.merchant.siteConfig)
                        .then(res => {
                            const updated = sanitizeSiteConfig(res);
                            document.title = res.companyName;

                            if (forced || updated || res.timeStamp !== state.site_config.timeStamp || !state.site_config.timeStamp) {
                                console.log('Updating Site Config')
                                dispatch({
                                    type: ActionType.SET_CONFIG,
                                    payload: res
                                });
                                functions.clearCache();
                                resolve(res);
                            }
                        })
                        .catch(ex => {
                            console.warn(ex)
                        })
                } catch (err) {
                    console.error('Failed to pull settings.')
                    reject(err);
                    throw err;
                }
            })
        },

        updateSiteConfig: (config) => dispatch({ type: ActionType.SET_CONFIG, payload: config }),

        clearCache: () => {
            return new Promise((resolve, reject) => {
                try {
                    caches
                        .keys()
                        .then((names) => names.forEach(e => caches.delete(e)));
                } catch (err) {
                    console.error('Failed to clear Cache.')
                    reject(err)
                    throw err;
                }
            })
        },

        getLocations: () => {
            return new Promise((resolve, reject) => {
                try {
                    HamQuest.get(QuestRoute.locations)
                        .then(res => {
                            dispatch({
                                type: ActionType.SET_LOCATIONS,
                                payload: res
                            });

                            HamQuest.get(QuestRoute.merchant.states)
                                .then(res => {
                                    dispatch({
                                        type: ActionType.SET_STATES,
                                        payload: res
                                    });
                                    resolve(res)
                                })
                        })

                } catch (err) {
                    throw err;
                }
            })
        },

        logIn: async (creds, limit = 0) => {
            const deviceId = await UseDeviceId()
            return new Promise((resolve, reject) => {
                let config = {
                    headers: {
                        deviceId,
                    },
                    captcha: window.siteKey,
                }

                try {
                    HamQuest.post(QuestRoute.user.login, creds, config)
                        .then(res => {
                            functions.updateLogIn(res);
                            functions.getUser();
                            resolve(true)
                        })
                        .catch(err => {
                            reject(err)
                        })
                } catch (err) {
                    console.error('Failed to Log In.')
                    reject(err)
                    throw err;
                }
            })
        },

        guestLogIn: () => {
            dispatch({
                type: ActionType.SET_GUEST_LOGIN,
                payload: true
            });
            functions.getLocations();
        },

        logOut: () => {
            HamQuest.clearUnauthorized();
            HamQuest.emptyQueue();
            functions.setQuestKeys(null, null);
            dispatch({
                type: ActionType.CLEAR_ACTIVE_USER,
                payload: {}
            });
        },

        getWashClub: () => {
            try {
                HamQuest.get(QuestRoute.user.washClub)
                    .then(res => {
                        dispatch({
                            type: ActionType.SET_WASH_CLUB,
                            payload: res
                        });
                    })
                    .catch(ex => {
                        console.warn(ex.blob);
                    })
            } catch (err) {
                throw err;
            }
        },

        getVehicle: () => {
            try {
                HamQuest.get(QuestRoute.user.vehicle)
                    .then(res => {
                        dispatch({
                            type: ActionType.SET_VEHICLE,
                            payload: res
                        });
                    })
                    .catch(ex => {
                        console.warn(ex.blob);
                    })
            } catch (err) {
                throw err;
            }
        },

        getUser: () => {
            try {
                HamQuest.get(QuestRoute.user.info)
                    .then(res => {
                        dispatch({
                            type: ActionType.SET_ACCOUNT_INFO,
                            payload: res
                        });
                        functions.setUserPayment(res.cctag);
                        functions.getLocations();
                        functions.getLocation();
                        functions.getWashClub();
                        functions.getVehicle();
                        functions.getForcedLocation();
                        functions.getUnreadNotifications();

                        if (isMobile())
                            getNotifPermissions();

                        if (!res.emailConfirmed) {
                            HamQuest.get(QuestRoute.user.confirmedEmail)
                                .then(confirmed => {
                                    if (confirmed) {
                                        functions.updateEmailConfirmation(true)
                                    } else {
                                        Modal.Show({
                                            title: 'Email Verification',
                                            body: `An email has been sent to ${res.email}. Please verify your email to complete your account setup.`,
                                            buttons: <Button
                                                onClick={() =>
                                                    Modal.Promise({
                                                        title: 'Sending a new Email',
                                                        callback: HamQuest.get(QuestRoute.user.confirmationEmail),
                                                        body: <Loading />
                                                    })
                                                        .then(() => Modal.Show({
                                                            title: 'Success',
                                                            body: `An new email has been sent to ${res.email}. Please verify your email to complete your account setup.`,
                                                            type: 'success'
                                                        }))
                                                        .catch((er) => Modal.Show({
                                                            title: 'Error',
                                                            body: `An error occurred when sending an email to ${res.email}. Please verify your email and try again.`,
                                                            type: 'error'
                                                        }))
                                                }
                                            >
                                                Resend Email
                                            </Button>
                                        })
                                    }
                                })
                        }
                    })
            } catch (err) {
                throw err;
            }
        },

        updateEmailConfirmation: (bool) => dispatch({ type: ActionType.SET_CONFIRMED_EMAIL, payload: bool }),

        // user payment methods
        getProcessor: async () => {
            try {
                return new Promise((resolve, reject) => {
                    HamQuest.get(QuestRoute.merchant.processor)
                        .then(res => {
                            dispatch({
                                type: ActionType.SET_PROCESSOR,
                                payload: res
                            });
                            resolve(res)
                        })
                })
            } catch (err) {
                throw err;
            }
        },

        getLocation: () => {
            try {
                return new Promise((resolve, reject) => {
                    if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition((position) => {
                            dispatch({
                                type: ActionType.SET_USER_LOCATION,
                                payload: {
                                    lat: position.coords.latitude,
                                    lng: position.coords.longitude
                                },
                            });
                            resolve({
                                lat: position.coords.latitude,
                                lng: position.coords.longitude
                            })
                        });
                    } else {
                        console.log('Geolocation disabled.')
                    }
                })
            } catch (err) {
                throw err;
            }
        },

        setUserPayment: payment => dispatch({ type: ActionType.SET_ACCOUNT_PAYMENT, payload: payment }),

        getUnreadNotifications: () => {
            HamQuest.get(QuestRoute.user.unreadNotifications)
                .then(res => {
                    if (res === state.active_user.notifications.unread) return;
                    dispatch({
                        type: ActionType.SET_UNREAD_NOTIFICATIONS,
                        payload: res
                    });
                })
        },

        getNotifications: () => {
            return new Promise((resolve, reject) => {
                HamQuest.postPost(QuestRoute.user.notifications)
                    .then(res => {
                        dispatch({
                            type: ActionType.SET_NOTIFICATIONS,
                            payload: res
                        });
                        resolve(res)
                    })
                    .finally(() => functions.clearNotifications())
            })
        },

        clearNotifications: () => dispatch({ type: ActionType.CLEAR_NOTIFICATIONS, }),

        addToGiftList: email => dispatch({ type: ActionType.ADD_GIFT_EMAIL, payload: email, }),

        setForcedLocation: location => {
            HamQuest.post(QuestRoute.user.locationAssignment, { locationId: location })
                .then(() => console.log('Location Assigned'))
                .catch((ex) => console.error(ex.blob))

            dispatch({
                type: ActionType.SET_FORCED_LOCATION,
                payload: parseInt(location),
            });
        },

        getForcedLocation: () => {
            HamQuest.get(QuestRoute.user.locationAssignment)
                .then((res) => {
                    dispatch({
                        type: ActionType.SET_FORCED_LOCATION,
                        payload: parseInt(res),
                    });
                })
                .catch((ex) => console.error(ex.blob))


        },

        // setMobileView: mobileView => {
        //     dispatch({
        //         type: 'SET_MOBILE_VIEW',
        //         payload: mobileView,
        //     });
        // },

        setActiveUser: (user) => dispatch({ type: ActionType.SET_ACTIVE_USER, payload: user, }),

        clearActiveUser: () => dispatch({ type: ActionType.CLEAR_ACTIVE_USER, }),

        setUserWallet: () => {
            try {
                return new Promise((resolve, reject) => {
                    HamQuest.get(QuestRoute.user.wallet)
                        .then(res => {
                            dispatch({
                                type: ActionType.SET_USER_WALLET,
                                payload: [...res]
                            });
                            resolve([...res])
                        })
                        .catch(ex => {
                            reject(ex);
                        })
                })
            } catch (err) {
                throw err;
            }
        },

        setOffline: (bool) => dispatch({ type: ActionType.SET_OFFLINE, payload: bool }),

        setTheme: (theme) => dispatch({ type: ActionType.SET_THEME, payload: theme }),

        setLightTheme: theme => dispatch({ type: ActionType.SET_LIGHT_THEME, payload: theme }),

        setDarkTheme: theme => dispatch({ type: ActionType.SET_DARK_THEME, payload: theme }),

        setThemes: themes => dispatch({ type: ActionType.SET_THEMES, payload: themes }),

        setHomeCards: config => dispatch({ type: ActionType.SET_HOME_CARDS, payload: config }),

        setBubbles: (bool) => dispatch({ type: ActionType.SET_BUBBLES, payload: bool }),

        setFontSize: (val) => dispatch({ type: ActionType.SET_FONT_SIZE, payload: val }),

        setLoading: (isLoading) => dispatch({ type: ActionType.SET_LOADING, payload: isLoading }),

        setCart: (cart) => dispatch({ type: ActionType.SET_CART, payload: cart }),
    });

    return {
        ...functions,
        state,
    };
}


const Reducer = (state = initialState, action) => {
    let newState = structuredClone(state);

    switch (action.type) {
        case ActionType.REHYDRATE_STATE:
            console.log('State Updated Elsewhere.')
            newState = structuredClone(action.payload);
            break;

        case ActionType.SET_APP_KEY:
            newState.app_key = action.payload;
            break;

        case ActionType.SET_CONFIG:
            newState.site_config = action.payload;
            newState.isLoading = false;
            break;

        case ActionType.SET_PROCESSOR:
            newState.processor_settings = action.payload;
            break;

        case ActionType.SET_LOCATIONS:
            newState.site_config.locations = action.payload;
            break;

        case ActionType.SET_STATES:
            newState.site_config.states = action.payload;
            break;

        case ActionType.SET_THEME:
            newState.dark_theme = action.payload;
            break;

        case ActionType.SET_THEMES:
            newState.site_config.themes = action.payload;
            break;

        case ActionType.SET_LIGHT_THEME:
            newState.site_config.lightTheme = action.payload;
            break;

        case ActionType.SET_DARK_THEME:
            newState.site_config.darkTheme = action.payload;
            break;

        case ActionType.SET_HOME_CARDS:
            newState.site_config.homeCards = action.payload;
            break;

        case ActionType.SET_BUBBLES:
            newState.site_config.bubbles.enabled = action.payload;
            break;

        case ActionType.SET_FONT_SIZE:
            newState.site_config.fontSize = action.payload;
            break;

        //USERS
        case ActionType.SET_ACCESS_TOKEN:
            newState.active_user = { ...newState.active_user, ...action.payload }
            newState.active_user.isLoggedIn = true;
            break;

        case ActionType.SET_GUEST_LOGIN:
            newState.active_user.isGuestUser = action.payload;
            break;

        case ActionType.SET_USER_LOCATION:
            newState.active_user.location = action.payload;
            break;

        case ActionType.SET_ACCOUNT_INFO:
            newState.active_user.account = action.payload;
            break;

        case ActionType.SET_ACCOUNT_PAYMENT:
            newState.active_user.payment = action.payload;
            break;

        case ActionType.CLEAR_ACTIVE_USER:
            HamQuest.clearHeaderKeys();
            const setup = initialState;
            setup.app_key = newState.app_key;
            setup.site_config = newState.site_config;
            setup.isLoading = false;
            newState = setup;
            break;

        case ActionType.SET_CONFIRMED_EMAIL:
            newState.active_user.account.emailConfirmed = action.payload;
            break;

        case ActionType.SET_UNREAD_NOTIFICATIONS:
            newState.active_user.notifications.unread = action.payload;
            break;

        case ActionType.SET_NOTIFICATIONS:
            newState.active_user.notifications.messages = action.payload;
            break;

        case ActionType.CLEAR_NOTIFICATIONS:
            newState.active_user.notifications.unread = 0;
            newState.active_user.notifications.messages = newState.active_user.notifications.messages.map(e => { return { ...e, seen: true } });
            break;

        case ActionType.SET_WASH_CLUB:
            newState.active_user.washClub = action.payload;
            break;

        case ActionType.SET_VEHICLE:
            newState.active_user.vehicle = action.payload;
            break;

        // user subscriptions
        case ActionType.GET_USER_SUBSCRIPTION:
            if (!action.payload) {
                newState.subscription.isSubscribed = false;
                newState.subscription.isLoading = false;
                break;
            }
            newState.subscription = action.payload;
            newState.subscription.isSubscribed = true;
            newState.subscription.isLoading = false;
            break;

        // user payment methods
        case ActionType.GET_USER_PAYMENT_METHOD:
            if (!action.payload) {
                newState.paymentMethod.hasPaymentMethod = false;
                newState.paymentMethod.isLoading = false;
                break;
            }
            newState.paymentMethod = action.payload;
            newState.paymentMethod.hasPaymentMethod = true;
            newState.paymentMethod.isLoading = false;
            break;

        case ActionType.SET_USER_WALLET:
            newState.active_user.wallet = action.payload;
            break;


        case ActionType.ADD_GIFT_EMAIL:
            newState.active_user.pastGifts = [...new Set([...newState.active_user.pastGifts, action.payload])];
            break;

        case ActionType.SET_FORCED_LOCATION:
            newState.active_user.forcedLocation = action.payload;
            break;

        case ActionType.SET_OFFLINE:
            newState.isOffline = action.payload;
            break;

        case ActionType.SET_ERROR:
            newState.error = { ...newState.error, ...action.payload };
            break;

        case ActionType.CLEAR_ERROR:
            newState.error = { title: '', message: '' };
            break;

        case ActionType.SET_LOADING:
            newState.isLoading = action.payload;
            break;

        case ActionType.SET_CART:
            newState.cart = action.payload;
            break;

        default:
            console.error(action.type)
            throw new Error();
    }
    return newState;
}

export default GlobalStateProvider;
